import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import { galleryCategories, galleryImages } from '@/mocks/gallery';

export default function GaleriePage() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('all');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const filteredImages = activeCategory === 'all'
    ? galleryImages
    : galleryImages.filter((img) => img.category === activeCategory);

  const openLightbox = useCallback((index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const goNext = useCallback(() => {
    setLightboxIndex((prev) => (prev + 1) % filteredImages.length);
  }, [filteredImages.length]);

  const goPrev = useCallback(() => {
    setLightboxIndex(
      (prev) => (prev - 1 + filteredImages.length) % filteredImages.length,
    );
  }, [filteredImages.length]);

  useEffect(() => {
    if (lightboxOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [lightboxOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, closeLightbox, goNext, goPrev]);

  const currentLightboxImage = filteredImages[lightboxIndex];

  return (
    <Layout>
      {/* Hero */}
      <section className="relative w-full h-[45vh] min-h-[320px] overflow-hidden flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20beauty%20salon%20interior%20with%20warm%20cream%20tones%2C%20soft%20golden%20lighting%2C%20a%20wall%20displaying%20framed%20before%20and%20after%20beauty%20transformation%20photos%2C%20professional%20hair%20styling%20tools%20arranged%20on%20a%20marble%20counter%2C%20fresh%20flowers%20in%20a%20ceramic%20vase%2C%20luxurious%20and%20inviting%20atmosphere%2C%20professional%20interior%20photography%2C%20warm%20ambient%20glow%2C%20high-end%20salon%20aesthetic%2C%20shallow%20depth%20of%20field%2C%20editorial%20quality&width=1600&height=720&seq=kds-gallery-hero&orientation=landscape"
            alt="Galerie KD'S BEAUTY"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/50 via-foreground-950/30 to-foreground-950/50" />
        </div>
        <div className="relative z-10 text-center px-4 w-full">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50/10 backdrop-blur-sm border border-background-50/20 mb-5">
            <span className="text-background-50/90 text-xs tracking-[0.15em] uppercase font-label">Portfolio</span>
          </div>
          <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl text-background-50 font-light mb-4">Galerie</h1>
          <p className="text-background-50/70 text-base md:text-lg max-w-2xl mx-auto">
            Découvrez nos plus belles réalisations, de la coiffure au maquillage en passant par les soins visage
          </p>
        </div>
      </section>

      {/* Gallery Content */}
      <section className="w-full py-16 md:py-24 bg-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          {/* Category Filter Tabs */}
          <div className="flex items-center justify-center mb-12 md:mb-16">
            <div className="inline-flex flex-wrap items-center justify-center gap-2 p-1.5 bg-background-100 rounded-full border border-background-200/60">
              {galleryCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 cursor-pointer ${
                    activeCategory === cat.id
                      ? 'bg-primary-500 text-background-50 shadow-sm'
                      : 'text-foreground-600 hover:text-foreground-800 hover:bg-background-50'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Masonry Grid */}
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 md:gap-6 max-w-7xl mx-auto">
            {filteredImages.map((img, index) => (
              <div
                key={img.id}
                onClick={() => openLightbox(index)}
                className="break-inside-avoid mb-5 md:mb-6 group cursor-pointer"
              >
                <div className="relative overflow-hidden rounded-2xl bg-background-100">
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    loading="lazy"
                  />
                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-foreground-950/0 group-hover:bg-foreground-950/30 transition-all duration-500 flex items-end">
                    <div className="w-full p-4 md:p-5 translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
                      <span className="inline-block px-3 py-1 text-xs font-medium bg-background-50/90 backdrop-blur-sm text-foreground-800 rounded-full mb-2">
                        {galleryCategories.find((c) => c.id === img.category)?.label}
                      </span>
                      <h3 className="text-sm font-medium text-background-50">{img.title}</h3>
                    </div>
                  </div>
                  {/* Zoom icon */}
                  <div className="absolute top-4 right-4 w-9 h-9 rounded-full bg-background-50/80 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-1 group-hover:translate-y-0">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-zoom-in-line text-foreground-700" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Empty state */}
          {filteredImages.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-image-line text-2xl text-foreground-400" />
              </div>
              <p className="text-foreground-500">Aucune image dans cette catégorie pour le moment.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="w-full py-14 md:py-20 bg-primary-500">
        <div className="w-full px-4 md:px-8 lg:px-12 text-center max-w-3xl mx-auto">
          <h2 className="font-heading text-3xl md:text-4xl text-background-50 mb-4">
            Prête à révéler votre beauté ?
          </h2>
          <p className="text-background-50/80 text-base mb-8 max-w-xl mx-auto">
            Nos expertes vous accueillent à Angré Château pour un moment de bien-être sur mesure
          </p>
          <Link
            to="/rdv"
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full text-sm font-medium bg-background-50 text-primary-600 hover:bg-background-50/95 transition-all duration-300 whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-calendar-line" />
            </div>
            Prendre rendez-vous
          </Link>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxOpen && currentLightboxImage && (
        <div
          className="fixed inset-0 z-[100] bg-foreground-950/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-5 right-5 md:top-8 md:right-8 w-11 h-11 rounded-full bg-background-50/10 backdrop-blur-sm border border-background-50/20 flex items-center justify-center text-background-50 hover:bg-background-50/20 transition-all z-10 cursor-pointer"
            aria-label="Fermer"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-close-line text-xl" />
            </div>
          </button>

          {/* Counter */}
          <div className="absolute top-5 left-5 md:top-8 md:left-8 z-10">
            <span className="px-3 py-1.5 text-xs font-medium bg-background-50/10 backdrop-blur-sm text-background-50/90 rounded-full">
              {lightboxIndex + 1} / {filteredImages.length}
            </span>
          </div>

          {/* Prev button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-background-50/10 backdrop-blur-sm border border-background-50/20 flex items-center justify-center text-background-50 hover:bg-background-50/20 transition-all z-10 cursor-pointer"
            aria-label="Précédent"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-left-line text-xl" />
            </div>
          </button>

          {/* Next button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-background-50/10 backdrop-blur-sm border border-background-50/20 flex items-center justify-center text-background-50 hover:bg-background-50/20 transition-all z-10 cursor-pointer"
            aria-label="Suivant"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-right-line text-xl" />
            </div>
          </button>

          {/* Image */}
          <div
            className="max-w-[90vw] max-h-[85vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={currentLightboxImage.src}
              alt={currentLightboxImage.alt}
              className="max-w-full max-h-[85vh] object-contain rounded-xl"
            />
          </div>

          {/* Info bar */}
          <div className="absolute bottom-5 md:bottom-8 left-1/2 -translate-x-1/2 z-10">
            <div className="bg-background-50/10 backdrop-blur-sm border border-background-50/20 rounded-full px-5 py-2 flex items-center gap-3">
              <span className="px-2 py-0.5 text-[10px] font-medium bg-primary-500/80 text-background-50 rounded-full uppercase">
                {galleryCategories.find((c) => c.id === currentLightboxImage.category)?.label}
              </span>
              <span className="text-sm text-background-50/90 font-medium">
                {currentLightboxImage.title}
              </span>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}